'use client';

import { usePathname } from 'next/navigation';
import { RefObject, useEffect } from 'react';

import { apiGtm } from '../lib/apiGtm';
import { GTMEcommerceItem } from '../lib/types';

interface GTMViewItemListProps {
  refItem: RefObject<HTMLElement | null>;
  item: GTMEcommerceItem;
}

const maxItems = 8;
const timeoutPush = 100;

let t = -1;
let items: GTMEcommerceItem[] = [];

// защита от повторной отправки одного и того же item
const firedItems = new Set<string>();

const getKey = (item: GTMEcommerceItem) =>
  `${item.item_id}-${item.item_list_name}-${item.position}`;

const debouncePushItem = (item: GTMEcommerceItem) => {
  const key = getKey(item);

  if (firedItems.has(key)) return;
  firedItems.add(key);

  const exists = items.some((i) => getKey(i) === key);
  if (exists) return;

  window.clearTimeout(t);

  items.push(item);

  if (items.length >= maxItems) {
    pushItems();
    return;
  }

  t = window.setTimeout(() => {
    pushItems();
  }, timeoutPush);
};

const pushItems = () => {
  if (items.length === 0) return;

  window.clearTimeout(t);

  apiGtm.viewItemList({
    ecommerce: {
      items: [...items].sort((a, b) => a.position - b.position)
    }
  });

  items = [];
};

export const GTMViewItemList = ({ refItem, item }: GTMViewItemListProps) => {
  const pathname = usePathname();

  useEffect(() => {
    return () => pushItems();
  }, [pathname]);

  useEffect(() => {
    const itemElement = refItem.current;
    if (!itemElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        observer.unobserve(itemElement);
        debouncePushItem(item);
      },
      { threshold: 0.5, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(itemElement);

    return () => observer.disconnect();
  }, [refItem, pathname, item]);

  return null;
};
